/* ──────────────────────────────────────────────────────────────
   src/pages/reports/asignaturas/AsignaturasAlumnosUnicosPage.tsx
   Informe: alumnado único por asignatura
   ────────────────────────────────────────────────────────────── */

import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Stack,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

import { useAsignaturasAlumnosUnicosReport } from '../../../hooks/useReportJob';
import type { AsignaturaAlumnosUnicosRow } from '../../../api/reports';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';

export default function AsignaturasAlumnosUnicosPage() {
  const { t } = useTranslation<'translation'>();
  const monday = dayjs().startOf('isoWeek');
  const sunday = monday.add(6, 'day');

  const [desde, setDesde] = useState(monday.format('YYYY-MM-DD'));
  const [hasta, setHasta] = useState(sunday.format('YYYY-MM-DD'));
  const [search, setSearch] = useState('');

  const [jobId, setJobId] = useState<string>();
  const { run, useStatus } = useAsignaturasAlumnosUnicosReport();
  const { data: status } = useStatus(jobId);

  const pendiente = !!status && !status.done && !status.failed;
  const rows: AsignaturaAlumnosUnicosRow[] = status?.result?.rows ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => r.asignatura.toLowerCase().includes(q));
  }, [rows, search]);

  const handleGenerate = async () => {
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      await downloadReportFile(status.result.url);
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('asigAlumnosUnicos.titulo')}</Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          type="date"
          label={t('asigAlumnosUnicos.filtros.desde')}
          size="small"
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          label={t('asigAlumnosUnicos.filtros.hasta')}
          size="small"
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={handleGenerate}>
          {t('asigAlumnosUnicos.filtros.generar')}
        </Button>

        {rows.length > 0 && (
          <TextField
            placeholder={t<string>('asigAlumnosUnicos.filtros.buscar')}
            size="small"
            sx={{ ml: 'auto', maxWidth: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Stack>

      {pendiente && status && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t('asigAlumnosUnicos.estado.generando', { progress: status.progress })}
          </Typography>
        </Stack>
      )}

      {status?.done && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Alert severity="success">
            {t('asigAlumnosUnicos.estado.exito', { count: rows.length })}
          </Alert>
          {status.result?.url && (
            <Button size="small" onClick={handleDownload}>
              {t('asigAlumnosUnicos.estado.descargar')}
            </Button>
          )}
        </Stack>
      )}
      {status?.failed && <Alert severity="error">{t('asigAlumnosUnicos.estado.error')}</Alert>}

      {status?.done && (
        <Paper>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {t('asigAlumnosUnicos.tabla.asignatura')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {t('asigAlumnosUnicos.tabla.alumnosUnicos')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {t('asigAlumnosUnicos.tabla.reservas')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {t('asigAlumnosUnicos.tabla.recurrencia')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row) => (
                  <TableRow key={row.asignaturaId} hover>
                    <TableCell>{row.asignatura}</TableCell>
                    <TableCell align="right">{row.alumnosUnicos}</TableCell>
                    <TableCell align="right">{row.reservasTotales}</TableCell>
                    <TableCell align="right">{row.recurrenciaPromedio}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Stack>
  );
}
